import {
  Badge,
  Box,
  Button,
  Card,
  CardHeader,
  Collapse,
  Flex,
  Grid,
  Heading,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  useTheme
} from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import { Hint } from 'BrokerTsLib/src/components/Hint'
import type { DiscountRequest, InsuranceParamsDto, OfferAddon, OfferDiscounts } from 'BrokerTsLib/src/dtos'
import { DiscountRequestState, Insurer, RiskVariant } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDateTime, priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import type { BaseSyntheticEvent } from 'react'
import { useCallback, useEffect, useState } from 'react'
import * as yup from 'yup'

import { OfferStatus, TabContent, useLoadingOverlay } from '@/components'
import { QuoteItemDiscountsForm } from '@/components/QuoteItem/QuoteItemDiscountsForm'
import { EditableTotal } from '@/containers/EditableTotal'
import { useAuth } from '@/context/AuthContext'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePartner } from '@/context/PartnerContext'
import { usePrintContext } from '@/context/PrintContext'
import { useSaveOfferDiscounts } from '@/hooks/useOffer'
import { useChangeParams, useChangePrice, useSetAddonSelectedVariant } from '@/hooks/useOfferAddon'
import { Check } from '@/icons'
import discountsActive from '@/images/discounts-active.svg'
import discountsImage from '@/images/discounts.svg'
import refreshIcon from '@/images/refresh-icon.svg'

import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { getLogo, SpecialLogoIds } from '@/utils/companiesLogos'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { Apis } from '../../../../../../appSettings'
import { useOffersAddonContext } from '../../../../../../context/OffersAddonProvider'
import { useUrl } from '../../../../../../hooks'
import { useGetOfferDocuments } from '../../../../../../hooks/useDocuments'
import { ExtendedInsurerOffers } from './ExtendedInsurerOffers'

export interface ExtendedInsuranceSimpleFormProps {
  offer: OfferAddon,
  spacing: number,
  defaultOpen?: boolean,
  defaultSelected?: boolean
  onChange?: (selected: boolean) => void
}

export const getActiveOffer = (offers: OfferAddon[]) => {
  return offers.find(x => x.isActive) || offers[0]
}

export const getParamsDataArr = (values: any) =>
  Object.keys(values).map((key) => {
    return { name: key, value: values[key] }
  })

export const ExtendedInsuranceForm = ({
  offer,
  spacing,
  defaultSelected,
  onChange,
  defaultOpen = false,
}: ExtendedInsuranceSimpleFormProps) => {
  const { translate } = useTranslationsContext()
  const { app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas
  const { user } = useAuth()
  const { printMode } = usePrintContext()
  const theme = useTheme()
  const documentUrl = useUrl(Apis.document)
  const { currentPartner } = usePartner()

  const { setLoading, startAnimation } = useLoadingOverlay()

  const { insuranceParams, refetchInsuranceParams, isDisabled, isContracted } = useInsuranceParams()
  const ipDto = insuranceParams as InsuranceParamsDto
  const { offers: allAddonOffers, calculatingOffersIds, updateOffer: updateAddonOffer, recalculateOffers: recalculateAddonOffers } = useOffersAddonContext()

  const { insurerNameFull, insurerId } = offer
  let logoInsurerId = insurerId
  if (currentPartner?.partner == 'Respect' && insurerId == Insurer.Custom) {
    logoInsurerId = SpecialLogoIds.RespectAssistance
  }
  const logo = getLogo(logoInsurerId)
  const isRecalculating = calculatingOffersIds?.includes(offer!.offerId) ?? false

  const { refetch: getOfferDocuments, data: offerDocuments } = useGetOfferDocuments(offer!.offerId, { enabled: false })
  useEffect(() => {
    if (!isRecalculating && user?.enableOfferXml) {
      // aby docitani dokumentu nebrzdilo jine dulezitejsi requesty. Do budoucna vracet bud vramci offery uz, nebo volat pres connection signalR
      setTimeout(() => getOfferDocuments(), 2000)
    }
  }, [isRecalculating, user?.enableOfferXml])

  const [selected, setSelected] = useState(defaultSelected)
  const { isOpen, onToggle, onClose, onOpen } = useDisclosure({ defaultIsOpen: defaultOpen })

  useEffect(() => {
    if (printMode)
      onOpen()
  }, [printMode])

  // aby se neroztahoval card pri kliku na popover apod...
  const stopPropagation = (e: BaseSyntheticEvent) => {
    e.stopPropagation()
  }

  const { mutateAsync: setAddonSelected } = useSetAddonSelectedVariant()

  const handleChange = (selected: boolean) => {
    if (selected) onOpen()
    else onClose()

    setSelected(selected)
    onChange?.(selected)

    setAddonSelected({ activeOfferId: offer!.offerId, selected: selected }).then(() =>
      refetchInsuranceParams(),
    )
  }

  const { mutateAsync: changePrice } = useChangePrice()

  const isAllriskPlusGap = (ipDto?.baseParams.hav?.findIndex((x) => x == RiskVariant.AllriskPlusGAP,) ?? -1) >= 0 && offer?.riskVariantId == RiskVariant.AddonGAP
  const defendEditPriceTooltip = 'Nastavení ceny má vliv na provizi, při zadání nejnižší možné částky je provize 0%'
  const allWithSameVariant = allAddonOffers.filter((x) => x.productId == offer?.productId) ?? []
  const allWithSameRiskVariant = allAddonOffers.filter((x) => x.riskVariantId == offer?.riskVariantId) ?? []

  const hasError = (offer?.errors?.length ?? 0) > 0
  const [currentStatus, setCurrentStatus] = useState<OfferStatus>(hasError ? OfferStatus.error : OfferStatus.completed)

  const { mutateAsync: changeParams } = useChangeParams()
  const { mutateAsync: saveDiscounts } = useSaveOfferDiscounts()

  // konverze z pole na object
  const defaultValues = offer.params?.parameterValues.reduce((obj: any, c) => {
    return { ...obj, [c.name]: c.value }
  }, {})

  const methods = useForm({ defaultValues: defaultValues })
  const {
    getValues,
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting }
  } = methods

  const discountsFormMethods = useForm<OfferDiscounts>({
    resolver: yupResolver(yup.object().shape({})),
    mode: 'onBlur',
    defaultValues: {
      discounts: [],
    },
  })

  const changedParams =
    getParamsDataArr(getValues()).filter(
      (x) =>
        (offer.params?.parameterValues.findIndex(
          (y) => y.name == x.name && y.value == x.value,
        ) ?? -1) < 0,
    ) ?? []

  const submitDiscounts = discountsFormMethods.handleSubmit((vals) => {
    if (isPublicPoleas) return Promise.resolve(vals) // v public poleas se slevy neukladaji

    return new Promise((resolve, reject) => {

      Promise.all([
        allWithSameRiskVariant.map((x) => {
          saveDiscounts({ data: vals, offerId: x.offerId })
        })
      ])
        .then((response) => {
          discountsFormMethods.reset(vals)
          resolve(vals)
        })
        .catch((err) => {
          reject(err ?? null)
          if (
            !(
              err.response &&
              err.response.status === 400 &&
              err.response.data
            )
          )
            return

          const data = err.response.data
          // zobrazeni serverove validace
          Object.keys(data).forEach((x: any) => {
            discountsFormMethods.setError(global.toCamelCase(x), {
              message: data[x].join(' '),
            })
          })
        })
    })
  })

  const submit = handleSubmit((values) => {
    setLoading(true)

    return new Promise((resolve, reject) => {

      Promise.all([
        submitDiscounts()
      ])
        .then(() => {
          // ulozim zmenu parametru
          changeParams({
            activeOfferId: offer.offerId,
            parameterValues: getParamsDataArr(values),
          })
            .then((resp) => {
              // vynutim rekalkulaci aktivni offery.
              // update v addonOffers se provede automaticky pres signalR
              recalculateAddonOffers([offer.offerId], true)
                .then(({ data }) => {
                  const status = !data[0].price ||
                    data[0].errors.filter((x) => !x.softError).length > 0
                    ? OfferStatus.error
                    : OfferStatus.completed

                  handleStatusChange(status)

                  if (status != OfferStatus.error)
                    setTabIndex(summaryTabIndex)

                  setLoading(false)
                  resolve(resp)
                })
                .catch(() => handleStatusChange(OfferStatus.error))
                .finally(() => {
                  setTimeout(() => {
                    startAnimation()
                  }, 500)
                })
                .catch(() => {
                  reject()
                })
            })
            .catch((err) => {
              reject(err)
              setLoading(false)
            })
        })
    })
  })

  const showReloadButton = changedParams.length > 0 || hasError || currentStatus == OfferStatus.changed
  const exludedFromPrint = !offer.print

  const summaryTabIndex = 0
  const [tabIndex, setTabIndex] = useState(summaryTabIndex)

  const [requestDiscountData, setRequestDiscountData] =
    useState<DiscountRequest>({
      state: DiscountRequestState.None,
      userCanApprove: false,
      discounts: [],
    })

  const handleTabsChange = (index: number) => {
    setTabIndex(index)
  }

  const allowDiscountsOverLimit = user?.allowDiscountsOverLimit ?? false

  const handleStatusChange = useCallback((status: OfferStatus) => {
    setCurrentStatus(status)
  }, [])

  useEffect(() => {
    if (discountsFormMethods.formState.isDirty)
      handleStatusChange(OfferStatus.changed)
  }, [discountsFormMethods.formState.isDirty])

  const showDiscountstab = !printMode
    && (offer.insurerId != Insurer.Colonnade || offer.riskVariantId == RiskVariant.AddonGAP) // u collonade pouze pro GAP (JAP-2290)
    && !isPublicPoleas

  return (
    <Card
      sx={{
        borderTop: printMode ? 'none' : '4px solid',
        borderTopColor: selected ? 'primary.500' : 'gray.300',
        transition: 'all .3s ease',
        cursor: printMode ? 'pointer' : undefined,
        boxShadow: printMode && !exludedFromPrint ? `0px 0px 6px 0px ${theme.colors.green[600]}` : 'var(--card-shadow)'
      }}
      onClick={(e) => {
        if (!printMode) return

        offer.print = !offer.print
        updateAddonOffer(offer)
      }}
    >
      <CardHeader px={4}>
        {printMode && !exludedFromPrint &&
          <Badge
            colorScheme="green"
            variant="solid"
            sx={{
              position: 'absolute',
              zIndex: '1', // pridano kvuli prekryti loading boxem
              top: -2,
              left: -2,
              boxShadow: 'md',
              transform: 'rotate(-3deg)',
            }}
          // {...rest}
          >
            <CustomTooltip label='Zahrnuto k tisku'>
              <Check />
            </CustomTooltip>
          </Badge>
        }

        <Flex
          sx={{
            alignItems: 'center',
            gap: 8,
            w: '100%',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={printMode ? undefined : () => {
            if (isOpen)
              handleTabsChange(summaryTabIndex) // pri zavirani nastavim tab na vychozi

            onToggle()
          }}
        >
          <HStack>
            {logo && <Image my={-2} src={logo} alt={insurerNameFull} sx={{ maxH: 12 }} />}

            {!logo && insurerNameFull}

            {isRecalculating && <Spinner />}
          </HStack>

          <Flex gap={4}>

            {hasError &&
              <CustomTooltip
                hasArrow
                trigger='clickHover'
                label={
                  <Stack direction={'column'}>
                    {offer?.errors.map((e, i) =>
                      <Text key={i} mx={2} my={1} variant='calcError' whiteSpace={'break-spaces'}>{e.message}</Text>
                    )}
                  </Stack>
                }>
                <Badge colorScheme="red">Chyba kalkulace</Badge>
              </CustomTooltip>
            }

            {(offerDocuments?.data && offerDocuments.data.length > 0) && user?.enableOfferXml && !printMode &&
              <Menu size="sm" placement='bottom'>
                <MenuButton sx={{ gap: 2, whiteSpace: 'nowrap' }}>
                  XML
                </MenuButton>
                <MenuList>
                  {offerDocuments.data.map(doc => (
                    <MenuItem key={doc.id} as={'a'} target='_blank' href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`}>
                      {doc.name} ({formatDateTime(doc.date, 'd.M.yyyy HH:mm:ss.SSS')})
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            }

            <Tabs variant="unstyled" index={tabIndex}>
              <TabList>
                {showDiscountstab &&
                  <Tab isDisabled={false} sx={{ padding: 0 }}>
                    <TabContent
                      label="Slevy"
                      isDisabled={false}
                      disabledTooltip={undefined}
                      image={discountsImage}
                      onClick={(e) => {
                        if (isOpen) {
                          e.stopPropagation()
                        }

                        handleTabsChange(tabIndex == 1 ? summaryTabIndex : 1)
                      }}
                      activeImage={
                        tabIndex === 1 ? discountsActive : undefined
                      }
                      showErrorBadge={
                        discountsFormMethods.formState.errors &&
                        (discountsFormMethods.formState.errors.discounts
                          ?.length ?? 0) > 0
                      }
                    />
                  </Tab>
                }
              </TabList>
            </Tabs>

            <Switch
              size="lg"
              disabled={
                hasError || printMode || isContracted
              }
              defaultChecked={false}
              isChecked={selected}
              onChange={(e) => handleChange(e.target.checked)}
            />
          </Flex>
        </Flex>
      </CardHeader>

      <FormProvider {...methods}>

        <Collapse in={isOpen}>
          <Tabs variant="unstyled" index={tabIndex} sx={{ height: '100%' }}>
            <TabPanels sx={{ height: '100%' }}>
              <TabPanel sx={{ height: '100%', py: 0 }}>
                {tabIndex === 0 && (
                  <ExtendedInsurerOffers
                    activeOffer={offer}
                    gridProps={{
                      sx: {
                        columnGap: 4,
                      }
                    }}
                  />
                )}
              </TabPanel>

              <TabPanel sx={{ height: '100%', py: 0 }}>
                {tabIndex === 1 && (
                  <>
                    <QuoteItemDiscountsForm
                      offer={offer}
                      formMethods={discountsFormMethods}
                    />
                  </>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>


        </Collapse>

        <Grid gridTemplateColumns='auto 1fr' gap={4} p={4}>
          {/* <Flex justifyContent='space-between' alignItems='center' p={4}> */}
          <HStack>
            <Text fontSize='md' fontWeight='bold'>{offer.name}</Text>

            {offer.productDescription && !printMode &&
              <Hint trigger='clickHover' maxW='md' placement="top" hasArrow>
                <Box dangerouslySetInnerHTML={{ __html: offer.productDescription }} />
              </Hint>
            }
          </HStack>

          {changedParams.length == 0 && !showReloadButton &&
            <EditableTotal
              label={''}
              price={offer.pricePerYear}
              color="transparent"
              tooltip={defendEditPriceTooltip}
              onChange={(value: number, successAction: () => void, errorAction: (error: string) => void) => {
                changePrice({ offerId: offer.offerId, price: value })
                  .then(({ data: updatedOffer }) => {
                    successAction()
                    updateAddonOffer(updatedOffer)
                  },
                  ).catch((reason) => {
                    let error = 'Nelze uložit změnu ceny'
                    if (reason.response.status == 400) {
                      error = reason.response.data
                    }
                    errorAction(error)
                  })
              }}
              priceMin={offer.priceMin}
              priceMax={offer.priceMax}
              size='sm'
              isEditable={
                isOpen &&
                !isDisabled &&
                !hasError &&
                !isAllriskPlusGap &&
                !printMode &&
                allWithSameVariant.length == 1 &&
                (!!offer.priceMax || !!offer.priceMin) // editovatelné jen pokud jsou nastavené min/max hodnoty
              }
              renderPrice={(price, handleEdit) => (
                <>
                  <Flex flexWrap='nowrap' alignItems='end' onClick={handleEdit}>
                    <Heading fontWeight='semibold' size='lg' color={selected ? undefined : 'gray.300'}>
                      {priceFormat(price, undefined, hasError ? 'N/A' : undefined)}
                    </Heading>
                    <Text ml={2} color={selected ? undefined : 'gray.300'} whiteSpace={'nowrap'}>/ rok</Text>
                  </Flex>
                </>
              )}
            />
          }

          {showReloadButton && (
            <Flex justifyContent="end">
              <Button
                variant="solid"
                isDisabled={isDisabled}
                isLoading={isSubmitting}
                loadingText="Přepočítávám..."
                onClick={() => submit()}
                gridColumnStart={1}
                sx={{ display: 'flex', gap: 2 }}
              >
                {translate('recalculate', 'Přepočítat')}

                <Image
                  src={refreshIcon}
                  alt={translate('recalculate', 'Přepočítat')}
                  ml='auto'
                />
              </Button>
            </Flex>
          )}
        </Grid>
      </FormProvider>
    </Card >
  )
}
